// ResultsOverlay.js
import React, { useEffect, useRef, useState } from 'react';
import './ResultsOverlay.css';
import { useTranslation } from 'react-i18next';
import useStore from '../../../store';
import skeletonImg from '../../../images/skeleton.jpg';

// Хелпер для нормализации ссылок (добавляет https:// если нет протокола)
const normalizeUrl = (url) => {
  url = url.trim();
  if (!url) return '';

  // Если начинается с http:// - заменить на https://
  if (url.startsWith('http://')) {
    return url.replace(/^http:\/\//, 'https://');
  }

  // Если не начинается с https:// - добавить его
  if (!url.startsWith('https://')) {
    return `https://${url}`;
  }

  return url;
};

// Хелпер для открытия ссылок
const openUrl = (url) => {
  const safeUrl = normalizeUrl(url);
  if (window.Telegram?.WebApp?.openLink) {
    window.Telegram.WebApp.openLink(safeUrl);
  } else {
    window.open(safeUrl, '_blank');
  }
};

// Компонент-заглушка с той же структурой, что и реальная реклама
const AdPlaceholder = () => {
  return (
    <div className="ad-content">
      <img src={skeletonImg} alt="Ad placeholder" className="ad-image" />
      <div
        className="ad-text"
        dangerouslySetInnerHTML={{ __html: window.t('results.ad_buy_promo') }}
        onClick={(e) => {
          if (e.target.tagName === 'A') {
            e.preventDefault();
            const href = e.target.getAttribute('href');
            openUrl(href);
          }
        }}
      />
    </div>
  );
};

function ResultsOverlay({
  store
  // setContinueDrawing,
 }) {

  const { t } = useTranslation();

  const { ref, canDraw } = store;
  const { info, work, telegram } = ref;
  const { game } = info;

  const { saveUserGameSets } = work.methods;

  // Достаем данные рекламы из нового adSlice
  const adData = useStore(state => state.adData);
  const socket = useStore(state => state.socket);
  const telegramUserId = telegram.telegramUserId;

  // Ref для предотвращения отправки viewAd несколько раз при ререндерах
  const viewSentRef = useRef(false);

  // Состояние для управления задержкой показа рекламы
  const [showAdContent, setShowAdContent] = useState(false);

  const results = game.results;

  // Определяем основной текст сообщения на основе существующих ключей
  let mainText = '';
  if (results.text) {
    mainText = results.text;
  } else if (results.moder) {
    mainText = t('results.moder', { moderName: results.moder.first_name });
  } else if (results.pass) {
    mainText = t('results.passed', { authorName: game.author.first_name });
  } else {
    // Стандартный случай победы
    mainText = t('results.winner', { winnerName: results.winner?.first_name });
  }

  const stayButtonLabel = canDraw ?
  t('results.continue') :
  t('results.stay');

  const handleAdClick = () => {
    if (adData?.button?.url) {
      openUrl(adData.button.url);
    }
  };

  // Эффект задержки показа - запускаем всегда при монтировании компонента
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAdContent(true);
    }, 1000); // 1 секунда задержки
    return () => clearTimeout(timer);
  }, []); // Пустой массив зависимостей - запускаем один раз при монтировании

  // Эффект отправки статистики (только когда реклама реально показана пользователю)
  useEffect(() => {
    if (adData && adData.id && showAdContent && !viewSentRef.current && socket) {
      socket.emit('viewAd', {
        adId: adData.id,
        userId: telegramUserId
      });
      viewSentRef.current = true;
    }
  }, [adData, showAdContent, socket, telegramUserId]);


  return (
    <>
      <div className='overlay-background'></div>
      <div className="results">

        {/* Заголовок: Текст + Слово в одну строку */}
        <div className="results-header">
           <span>{mainText}</span>
           {game.word && (
             <>
               {/* Пробел перед словом */}
               <span> </span>
               <b>{game.word?.toUpperCase?.() || '...'}</b>
             </>
           )}
        </div>

        {/* Строка спонсора */}
        <div className="results-sponsor-line">
           {t('results.sponsor_title')}
        </div>

        {/* --- БЛОК РЕКЛАМЫ --- */}
        <div className="ad-container">
          {!showAdContent ? (
            // ЗАГЛУШКА (показывается первую секунду)
            <AdPlaceholder />
          ) : adData ? (
            // РЕАЛЬНЫЙ КОНТЕНТ (появляется плавно)
            <div className="ad-content fade-in">
              {adData.imageUrl && (
                <img src={adData.imageUrl} alt="Ad" className="ad-image" />
              )}
              <div
                className="ad-text"
                dangerouslySetInnerHTML={{ __html: adData.text }}
                onClick={(e) => {
                  if (e.target.tagName === 'A') {
                    e.preventDefault();
                    const href = e.target.getAttribute('href');
                    openUrl(href);
                  }
                }}
              />
              {/* Рендеринг клавиатуры кнопок */}
              {adData.keyboard && adData.keyboard.length > 0 ? (
                <div className="ad-keyboard">
                  {adData.keyboard.map((row, rowIndex) => (
                    <div key={rowIndex} className="ad-keyboard-row">
                      {row.map((btn, btnIndex) => (
                        <button
                          key={btnIndex}
                          className="ad-button"
                          onClick={() => {
                            if (btn.url) openUrl(btn.url);
                          }}
                        >
                          {btn.text}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                /* Обратная совместимость для старой структуры */
                adData.button && (
                  <button className="ad-button" onClick={handleAdClick}>
                    {adData.button.text}
                  </button>
                )
              )}
            </div>
          ) : (
            // ЗАГЛУШКА ПОКА НЕТ РЕКЛАМЫ (после задержки)
            <AdPlaceholder />
          )}
        </div>
        {/* -------------------- */}

        {/* Кнопки в один ряд */}
        <div className="results-actions-row">
          <button
            className="results-button-compact"
            onClick={() => saveUserGameSets({ resultsViewedAt: Date.now()})}>
            {stayButtonLabel}
          </button>
          <button
            className="results-button-compact"
            onClick={window.Telegram.WebApp.close}>
            {t('results.close')}
          </button>
        </div>
      </div>
    </>
  );
}

export default ResultsOverlay;
